
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { DashboardStats, MembershipByState } from '../DashboardStats';

const StatsSection = () => {
  const [memberStats, setMemberStats] = useState({
    total: 0,
    pending: 0,
    byState: [] as {state: string, count: number}[]
  });
  const [messageCount, setMessageCount] = useState(0);
  const [businessCount, setBusinessCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();

    // Set up realtime subscription for any changes to the stats
    const channel = supabase
      .channel('admin-stats-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'community_members'
        },
        () => {
          fetchStats();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      console.log('[StatsSection] Starting to fetch stats...');
      
      // Get total member count
      const { count: totalCount, error: totalError } = await supabase
        .from('community_members')
        .select('*', { count: 'exact', head: true });
      
      if (totalError) {
        console.error('[StatsSection] Error fetching total count:', totalError);
        throw totalError;
      }
      
      // Get pending member count
      const { count: pendingCount, error: pendingError } = await supabase
        .from('community_members')
        .select('*', { count: 'exact', head: true })
        .eq('approved', false);
      
      if (pendingError) {
        console.error('[StatsSection] Error fetching pending count:', pendingError);
        throw pendingError;
      }
      
      // Get message count
      const { count: announceCount, error: announceError } = await supabase
        .from('community_announcements')
        .select('*', { count: 'exact', head: true });
      
      if (announceError) {
        console.error('[StatsSection] Error fetching announcements count:', announceError);
        throw announceError;
      }
      
      // Get business count
      const { count: businessesCount, error: businessError } = await supabase
        .from('community_marketplace')
        .select('*', { count: 'exact', head: true })
        .eq('approved', true);
      
      if (businessError) {
        console.error('[StatsSection] Error fetching business count:', businessError);
        throw businessError;
      }
      
      // Get members by state
      const { data: stateData, error: stateError } = await supabase
        .from('community_members')
        .select('state')
        .eq('approved', true);
      
      if (stateError) {
        console.error('[StatsSection] Error fetching state data:', stateError);
        throw stateError;
      }
      
      // Count members by state
      const stateCounts = (stateData || []).reduce((acc: Record<string, number>, member) => {
        if (member.state) {
          acc[member.state] = (acc[member.state] || 0) + 1;
        }
        return acc;
      }, {});
      
      const stateStats = Object.entries(stateCounts).map(([state, count]) => ({
        state,
        count: count as number
      })).sort((a, b) => b.count - a.count);
      
      console.log('[StatsSection] Successfully fetched all stats');
      
      setMemberStats({
        total: totalCount || 0,
        pending: pendingCount || 0,
        byState: stateStats
      });
      
      setMessageCount(announceCount || 0);
      setBusinessCount(businessesCount || 0);
    } catch (error) {
      console.error('[StatsSection] Error fetching stats:', error);
      toast.error('Failed to load statistics. Please try refreshing the page.');
      
      // Set default values on error
      setMemberStats({
        total: 0,
        pending: 0,
        byState: []
      });
      setMessageCount(0);
      setBusinessCount(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <DashboardStats 
        memberStats={memberStats}
        messageCount={messageCount}
        businessCount={businessCount}
        loading={loading}
      />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <MembershipByState memberStats={memberStats} loading={loading} />
      </div>
    </div>
  );
};

export default StatsSection;
